// src/utils/csvValidator.js

/**
 * Expected main headers (required headers with *)
 */
const REQUIRED_MAIN_HEADERS = [
  "Mortgage Term (years) *",
  "Product Term (Years) *",
  "LTV (%) *",
  "Min Loan (£) *",
  "Max Loan (£) *",
  "Product Rate (%) *",
  "APRC (%) *",
  "SVR Margin (%) *",
  "Basic Tax Payer DSC (%) *",
  "High Tax Payer DSC (%) *",
  "ICR Rate (%) *",
  "Product Fee *",
  "Application Fee (£) *",
  "Valuation Fee",
  "Legal Fee",
  "Early Repayment Charges",
  "Cashback (£)",
  "Product Name *",
  "Product Type *",
  "Lender Product Code *",
  "Product Expiry Date *", // Optional - can be empty or zero
  "Loan Purpose *",
  "Borrower Type *",
  "EPC rating — Is it a green product? *",
  "Home Owner *",
  "BTL Experience (years) *",
  "Credit Worthiness *",
  "Tenancy Type *",
  "F/H or L/H *",
  "Ex Local Authority *",
  "Deposit Origination *",
  "Day 1 Remortgage *",
  "Portable",
  "Overpayments",
  "Underpayments",
  "Payment Holiday",
  "Free Legals",
  "Free Valuation",
];

/**
 * Parse a CSV line handling quoted values
 */
function parseCSVLine(line) {
  const result = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  result.push(current.trim());

  return result;
}

/**
 * Expected subheaders (row 2) based on the template
 */
const EXPECTED_SUBHEADERS = [
  "", "", "", "", "", "", "", "", "", "", "", // First 11 columns (empty)
  "£", "%", "Mini Fee", "", // Product Fee sub-columns
  "", // Application Fee
  "Scale", "Value/TBC", // Valuation Fee
  "Scale", "Value/TBC", // Legal Fee
  "", // Early Repayment Charges
  "Year 1 (%)", "Year 2 (%)", "Year 3 (%)", "Year 4 (%)", "Year 5 (%)", // ERC years
  "", // Cashback
  "", // Product Name
  "", // Product Type
  "Fixed", "Variable", "Tracker", "Discounted", // Product Type options
  "", // Lender Product Code
  "dd/mm/yyyy", // Product Expiry Date format hint
  "", // Loan Purpose
  "Purchase", "Remortgage", "Product Transfer", // Loan Purpose options
  "", // Borrower Type
  "Individual/Partnership", "Ltd Co.", "Pension/Trust", // Borrower Type options
  "", // EPC rating
  "Yes", "No", // EPC options
  "", // Home Owner
  "Yes", "No", // Home Owner options
  "", // BTL Experience
  "0", "01-May", "5+", // BTL Experience options
  "", // Credit Worthiness
  "Excellent 881+", "Fair 721-880", "Poor <550", // Credit Worthiness options
  "", // Tenancy Type
  "AST", "Lease", "Licence", "None", // Tenancy Type options
  "", // F/H or L/H
  "Freehold", "Leasehold", // Tenure options
  "", // Leasehold remaining term
  "Years", // Leasehold remaining term subheader
  "", // Ex Local Authority
  "Yes", "No", // Ex Local Authority options
  "", // Deposit Origination
  "Savings", "Equity", "Gifted", // Deposit Origination options
  "", // Day 1 Remortgage
  "Pound for Pound", "Market Value", "Refurb", // Day 1 Remortgage options
  "", // Portable
  "Yes", "No", // Portable options
  "", // Overpayments
  "Yes", "No", // Overpayments options
  "", // Underpayments
  "Yes", "No", // Underpayments options
  "", // Payment Holiday
  "Yes", "No", // Payment Holiday options
  "", // Free Legals
  "Yes", "No", // Free Legals options
  "", // Free Valuation
  "Yes", "No", // Free Valuation options
];

const HEADER_ROW_MARKERS = ["Mortgage Term (years) *", "Product Name *", "Lender Product Code *", "LTV (%) *"];
const HEADER_SEARCH_LIMIT = 10;

function isHeaderRow(values) {
  return HEADER_ROW_MARKERS.filter(h => findColumnIndex(values, h) !== -1).length >= 3;
}

/**
 * Build header/subheader/data structure from a grid of { rowNum, values }.
 * The header row may be preceded by a title row (e.g. "Latest_BTL_CSV_template").
 */
function buildTable(grid) {
  const nonEmpty = grid.filter(r => r.values.some(v => v && String(v).trim()));
  const headerPos = nonEmpty.slice(0, HEADER_SEARCH_LIMIT).findIndex(r => isHeaderRow(r.values));
  if (headerPos === -1) {
    throw new Error("Could not find the header row (e.g. \"Mortgage Term (years) *\", \"Product Name *\")");
  }
  if (nonEmpty.length < headerPos + 2) {
    throw new Error("File must have a header row, a subheader row, and at least one data row");
  }

  const headerRow = nonEmpty[headerPos];
  const subheaderRow = nonEmpty[headerPos + 1];
  const width = Math.max(headerRow.values.length, subheaderRow.values.length);
  const pad = (values) => Array.from({ length: Math.max(width, values.length) }, (_, i) => (values[i] || "").trim());

  const headers = pad(headerRow.values);
  const subheaders = pad(subheaderRow.values);
  const rows = nonEmpty.slice(headerPos + 2).map(({ rowNum, values }) => {
    const row = {};
    headers.forEach((header, idx) => {
      if (header) row[header] = (values[idx] || "").trim();
    });
    row._raw = pad(values);
    row._headers = headers;
    row._subheaders = subheaders;
    row._rowNum = rowNum;
    return row;
  });

  return { headers, subheaders, rows, dataStartRow: subheaderRow.rowNum + 1 };
}

/**
 * Parse CSV file content
 */
export function parseCSV(content) {
  const grid = content
    .replace(/^\uFEFF/, "")
    .split(/\r?\n/)
    .map((line, i) => ({ rowNum: i + 1, values: line.trim() ? parseCSVLine(line) : [] }));
  return buildTable(grid);
}

/**
 * Parse XLSX file content (first worksheet)
 */
export async function parseXLSX(arrayBuffer) {
  const { readXlsxRows } = await import("./xlsxReader.js");
  return buildTable(await readXlsxRows(arrayBuffer));
}

/**
 * Find column index by header name (allows reordering)
 * Handles variations like missing asterisks, extra spaces, etc.
 */
function findColumnIndex(headers, headerName) {
  // First try exact match
  let idx = headers.findIndex(h => h === headerName);
  if (idx !== -1) return idx;

  // Normalize header name for comparison (remove asterisks and extra spaces)
  // "\uFFFD" appears when a "£" was saved in a non-UTF-8 encoding
  const normalize = (str) => {
    if (!str) return "";
    return String(str).replace(/\uFFFD/g, "£").replace(/\s*\*\s*/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
  };
  const normalizedTarget = normalize(headerName);

  // Try normalized match
  idx = headers.findIndex(h => normalize(h) === normalizedTarget);
  if (idx !== -1) return idx;

  // Try partial match (for cases like "F/H or L/H" vs "F/H or L/H *")
  idx = headers.findIndex(h => {
    const normalized = normalize(h);
    if (!normalized) return false;
    return normalized.includes(normalizedTarget) ||
      normalizedTarget.includes(normalized);
  });

  return idx;
}

/**
 * Get value from row by header name
 */
function getValueByHeader(row, headerName) {
  const idx = findColumnIndex(row._headers, headerName);
  if (idx === -1) return "";
  return trimText(row._raw[idx] || "");
}

/**
 * Get values for a group of consecutive columns starting after a main header
 * (Unused but kept for reference)
 */
/*
function getGroupValues(row, mainHeaderName, count) {
  const mainIdx = findColumnIndex(row._headers, mainHeaderName);
  if (mainIdx === -1) return [];
  const values = [];
  for (let i = 1; i <= count; i++) {
    values.push(trimText(row._raw[mainIdx + i] || ""));
  }
  return values;
}
*/

/**
 * Trim whitespace from text fields
 */
function trimText(value) {
  return typeof value === "string" ? value.trim() : String(value || "").trim();
}

function stripCommas(value) {
  return typeof value === "string" ? value.replace(/,/g, "") : String(value || "");
}

/**
 * Check if value is empty or NaN
 * (Unused but kept for reference)
 */
/*
function isEmptyOrNaN(value) {
  const trimmed = trimText(value);
  if (trimmed === "") return true;
  const num = Number(trimmed);
  return isNaN(num);
}
*/

/**
 * Check if value is a valid number
 */
function isValidNumber(value, min = null, max = null) {
  const trimmed = stripCommas(trimText(value));
  if (trimmed === "") return false;
  const num = Number(trimmed);
  if (isNaN(num)) return false;
  if (min !== null && num < min) return false;
  if (max !== null && num > max) return false;
  return true;
}

/**
 * Check if value is a valid integer
 * (Unused but kept for reference)
 */
/*
function isValidInteger(value, min = null, max = null) {
  if (!isValidNumber(value, min, max)) return false;
  const num = Number(trimText(value));
  return Number.isInteger(num);
}
*/

/**
 * Check if value is an integer (mirrored from ManualEntryPage)
 */
function isInteger(value) {
  const trimmed = stripCommas(trimText(value));
  if (trimmed === "") return false;
  return String(Number(trimmed)) === String(parseInt(trimmed, 10));
}

/**
 * Validate date in dd/mm/yyyy format
 * (Unused but kept for reference)
 */
/*
function isValidDateDDMMYYYY(dateStr) {
  if (!dateStr || typeof dateStr !== "string") return false;
  const trimmed = trimText(dateStr);
  if (!trimmed) return false;

  const parts = trimmed.split("/");
  if (parts.length !== 3) return false;

  const [day, month, year] = parts.map(Number);
  if (isNaN(day) || isNaN(month) || isNaN(year)) return false;
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;

  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return false;
  }

  // Check not in past
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (date < today) return false;

  return true;
}
*/

/**
 * Check if value is Yes (case-insensitive)
 */
function isYes(value) {
  return trimText(value).toLowerCase() === "yes";
}

/**
 * Count how many Yes values in an array
 */
function countYes(values) {
  return values.filter(v => isYes(v)).length;
}

/**
 * Check if a column is selected based on value and subheader
 * Supports two formats:
 * 1. "Yes" in any column (traditional format)
 * 2. Value matches subheader (e.g., "No" in column with "No" subheader)
 */
function isColumnSelected(value, subheader) {
  const trimmedValue = trimText(value).toLowerCase();
  const trimmedSubheader = trimText(subheader).toLowerCase();
  
  // Method 1: Value is "Yes"
  if (trimmedValue === "yes") {
    return true;
  }
  
  // Method 2: Value matches the subheader
  // (e.g., "No" in the "No" column, "0" in the "0" column)
  if (trimmedValue !== "" && trimmedValue === trimmedSubheader) {
    return true;
  }
  
  return false;
}

/**
 * Count how many columns are selected using flexible matching
 * Takes arrays of values and corresponding subheaders
 */
function countSelected(values, subheaders) {
  let count = 0;
  for (let i = 0; i < values.length; i++) {
    if (isColumnSelected(values[i], subheaders[i])) {
      count++;
    }
  }
  return count;
}

/**
 * First column of a header's option group. Newer templates put the first
 * sub-header under the main header itself; older ones leave it blank.
 */
function groupStart(subheaders, mainIdx) {
  return trimText(subheaders[mainIdx] || "") !== "" ? mainIdx : mainIdx + 1;
}

/**
 * Complex BTL column groups (coloured columns at the end of the BTL template).
 * A row is Complex BTL when any of these is filled; otherwise it is plain BTL.
 */
export const PRODUCT_CATEGORY = { btl: "BTL", complex: "CBTL" };

const COMPLEX_BTL_GROUPS = [
  { key: "hmo", label: "HMO", match: /^hmo\b/ },
  { key: "mufb", label: "MUFB", match: /^mufb\b/ },
  { key: "mpot", label: "MPOT", match: /^mpot\b/ },
  { key: "holidayLets", label: "Holiday Lets", match: /^holiday lets?\b/ },
  { key: "shortTermLets", label: "Short Term Lets", match: /^short term lets?\b/ },
  { key: "portfolio", label: "Portfolio", match: /^portf?olio\b/ },
];

// Guidance text the template ships with in the first data row (e.g. "0-100", "Yes/No", "%")
function isGuidanceValue(value) {
  const v = trimText(value).toLowerCase();
  return v === "yes/no" || v === "yes / no" || v === "%" || /^\d+\s*-\s*\d+$/.test(v);
}

function complexColumnRule(subheader) {
  const s = trimText(subheader).toLowerCase();
  if (/weeks?/.test(s)) return { kind: "number", min: 0, max: 52, integer: false };
  if (/%|retained/.test(s)) return { kind: "number", min: 0, max: 100, integer: false };
  if (/\bmax\b|no\. of|number of|rooms|units/.test(s)) return { kind: "number", min: 0, max: 100, integer: true };
  return { kind: "yesno" };
}

// The guidance row is more reliable than sub-header wording (the template has typos like "bv").
function guidanceColumnRule(guidance, fallback) {
  const v = trimText(guidance).toLowerCase();
  if (v === "yes/no" || v === "yes / no") return { kind: "yesno" };
  if (v === "%") return { kind: "number", min: 0, max: 100, integer: false };
  const range = v.match(/^(\d+)\s*-\s*(\d+)$/);
  if (range) {
    return {
      kind: "number",
      min: Number(range[1]),
      max: Number(range[2]),
      integer: fallback.kind === "number" ? fallback.integer : true,
    };
  }
  return fallback;
}

function findComplexColumns(headers, subheaders, rows = []) {
  const guidanceFor = (col) => {
    const hit = rows.find(r => isGuidanceValue(r._raw?.[col] || ""));
    return hit ? hit._raw[col] : "";
  };
  const groupEnd = (from) => {
    let end = from + 1;
    while (end < headers.length && trimText(headers[end]) === "") end++;
    return end;
  };

  return COMPLEX_BTL_GROUPS.map((group) => {
    const idx = headers.findIndex(h => group.match.test(trimText(h).toLowerCase()));
    if (idx === -1) return null;
    const end = groupEnd(idx);
    const columns = [];
    for (let col = idx; col < end; col++) {
      const sub = trimText(subheaders[col] || "");
      if (sub === "") continue;
      columns.push({ col, subheader: sub, rule: guidanceColumnRule(guidanceFor(col), complexColumnRule(sub)) });
    }
    return columns.length ? { ...group, columns } : null;
  }).filter(Boolean);
}

/**
 * Validate CSV file
 */
export async function validateCSVFile(file) {
  const errors = [];
  const warnings = [];

  // 1) File + structure validations (hard fail)
  if (!file) {
    errors.push("No file selected");
    return { valid: false, errors, warnings, data: null, stats: null };
  }

  const validTypes = [".csv", ".xlsx"];
  const fileExtension = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();

  if (!validTypes.includes(fileExtension)) {
    errors.push(`Invalid file type. Expected: ${validTypes.join(", ")}`);
    return { valid: false, errors, warnings, data: null, stats: null };
  }

  const maxSize = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSize) {
    errors.push(`File size (${(file.size / 1024 / 1024).toFixed(2)}MB) exceeds maximum of 10MB`);
    return { valid: false, errors, warnings, data: null, stats: null };
  }

  const isXlsx = fileExtension === ".xlsx";
  let parsed;
  try {
    parsed = isXlsx ? await parseXLSX(await file.arrayBuffer()) : parseCSV(await file.text());
  } catch (err) {
    errors.push(`Failed to read ${isXlsx ? "XLSX" : "CSV"} file: ${err.message}`);
    return { valid: false, errors, warnings, data: null, stats: null };
  }

  const { headers, subheaders, rows, dataStartRow } = parsed;
  const complexGroups = findComplexColumns(headers, subheaders, rows);
  const complexCounts = Object.fromEntries(complexGroups.map(g => [g.key, 0]));
  let complexRowCount = 0;
  const productCategories = {};

  // Validate column count (warn if significantly different, but don't hard fail)
  // Note: CSV files may have fewer columns than the template (74 vs 94 is acceptable)
  const expectedColumnCount = EXPECTED_SUBHEADERS.length;
  if (headers.length < 50) {
    // Previously pushed a warning about low column count; now suppressed.
  } else if (headers.length !== expectedColumnCount) {
    // Previously pushed a warning about column count mismatch; now suppressed.
  }

  if (subheaders.length !== headers.length) {
    errors.push(`Subheader column count (${subheaders.length}) does not match header column count (${headers.length})`);
  }

  // Validate required headers exist (allows reordering but warns)
  const missingHeaders = [];
  REQUIRED_MAIN_HEADERS.forEach(header => {
    const idx = findColumnIndex(headers, header);
    if (idx === -1) {
      missingHeaders.push(header);
    }
  });

  if (missingHeaders.length > 0) {
    errors.push(`Missing required headers: ${missingHeaders.join(", ")}`);
  }

  // Validate subheaders match template (check key subheaders by finding main headers first)
  // This is more flexible and handles column reordering
  const subheaderMismatches = [];

  // Key subheader validations (find by main header position)
  const keySubheaderChecks = [
    { mainHeader: "Product Fee *", offsets: [1, 2, 3], expected: ["£", "%", "Mini Fee"] },
    { mainHeader: "Valuation Fee", offsets: [1, 2], expected: ["Scale", "Value/TBC"] },
    { mainHeader: "Legal Fee", offsets: [1, 2], expected: ["Scale", "Value/TBC"] },
    { mainHeader: "Early Repayment Charges", offsets: [1, 2, 3, 4, 5], expected: ["Year 1 (%)", "Year 2 (%)", "Year 3 (%)", "Year 4 (%)", "Year 5 (%)"] },
    { mainHeader: "Product Type *", offsets: [1, 2, 3, 4], expected: ["Fixed", "Variable", "Tracker", "Discounted"] },
    { mainHeader: "Product Expiry Date *", offsets: [1], expected: ["dd/mm/yyyy"] },
    { mainHeader: "Loan Purpose *", offsets: [1, 2, 3], expected: ["Purchase", "Remortgage", "Product Transfer"] },
    { mainHeader: "Borrower Type *", offsets: [1, 2, 3], expected: ["Individual/Partnership", "Ltd Co.", "Pension/Trust"] },
    { mainHeader: "EPC rating — Is it a green product? *", offsets: [0, 1], expected: ["Yes", "No"] },
    { mainHeader: "Home Owner *", offsets: [0, 1], expected: ["Yes", "No"] },
    { mainHeader: "BTL Experience (years) *", offsets: [0, 1, 2], expected: ["0", "01-May", "5+"] },
    { mainHeader: "Credit Worthiness *", offsets: [0, 1, 2], expected: ["Excellent 881+", "Fair 721-880", "Poor <550"] },
    { mainHeader: "Tenancy Type *", offsets: [0, 1, 2, 3], expected: ["AST", "Lease", "Licence", "None"] },
    { mainHeader: "F/H or L/H *", offsets: [0, 1], expected: ["Freehold", "Leasehold"] },
    { mainHeader: "Leasehold remaining term", offsets: [0], expected: ["Years"] },
    { mainHeader: "Ex Local Authority *", offsets: [0, 1], expected: ["Yes", "No"] },
    { mainHeader: "Deposit Origination *", offsets: [1, 2, 3], expected: ["Savings", "Equity", "Gifted"] },
    { mainHeader: "Day 1 Remortgage *", offsets: [1, 2, 3], expected: ["Pound for Pound", "Market Value", "Refurb"] },
    { mainHeader: "Portable", offsets: [0, 1], expected: ["Yes", "No"] },
    { mainHeader: "Overpayments", offsets: [0, 1], expected: ["Yes", "No"] },
    { mainHeader: "Underpayments", offsets: [0, 1], expected: ["Yes", "No"] },
    { mainHeader: "Payment Holiday", offsets: [0, 1], expected: ["Yes", "No"] },
    { mainHeader: "Free Legals", offsets: [0, 1], expected: ["Yes", "No"] },
    { mainHeader: "Free Valuation", offsets: [0, 1], expected: ["Yes", "No"] },
  ];

  keySubheaderChecks.forEach(({ mainHeader, offsets, expected }) => {
    const mainIdx = findColumnIndex(headers, mainHeader);
    if (mainIdx === -1) return; // Main header not found, skip

    offsets.forEach((offset, i) => {
      const subIdx = mainIdx + offset;
      if (subIdx < subheaders.length) {
        const actual = trimText(subheaders[subIdx]);
        const expectedVal = expected[i];
        if (expectedVal && actual !== expectedVal) {
          subheaderMismatches.push(`${mainHeader} sub-column ${offset}: Expected "${expectedVal}", found "${actual}"`);
        }
      }
    });
  });

  // Subheader mismatches are WARNINGS only - CSV files may have different column orders
  // This is acceptable as long as the data validation passes
  if (subheaderMismatches.length > 0) {
    // Previously added a warning summarizing subheader mismatches; now suppressed.
  }

  // Check for at least 1 product row (row 3 onwards)
  if (rows.length === 0) {
    errors.push(`File contains no data rows. At least one product row is required (starting from row ${dataStartRow}).`);
    return { valid: false, errors, warnings, data: parsed, stats: { totalRows: 0, btlRows: 0, complexBtlRows: 0, totalHeaders: headers.length } };
  }

  rows.forEach((row) => {
    const rowNum = row._rowNum;

    // Check for completely empty rows
    const hasAnyData = row._raw.some(v => v && trimText(v) !== "");
    if (!hasAnyData) {
      errors.push(`Row ${rowNum}: Row is completely empty`);
      return; // Skip further validation for this row
    }

    // 1b) Complex BTL columns: optional for BTL rows; any positive number or "Yes" marks the row as Complex BTL
    const complexTypes = [];
    complexGroups.forEach((group) => {
      let groupSelected = false;
      group.columns.forEach(({ col, subheader, rule }) => {
        const value = trimText(row._raw[col] || "");
        if (value === "" || isGuidanceValue(value)) return;
        const field = `${group.label} — ${subheader}`;

        if (rule.kind === "yesno") {
          const v = value.toLowerCase();
          if (v !== "yes" && v !== "no") {
            errors.push(`Row ${rowNum}: "${field}" must be "Yes", "No", or empty`);
          } else if (v === "yes") {
            groupSelected = true;
          }
          return;
        }

        const n = Number(stripCommas(value));
        if (isNaN(n)) {
          errors.push(`Row ${rowNum}: "${field}" must be a number`);
        } else if (n < rule.min || n > rule.max) {
          errors.push(`Row ${rowNum}: "${field}" must be between ${rule.min} and ${rule.max}`);
        } else if (rule.integer && !Number.isInteger(n)) {
          errors.push(`Row ${rowNum}: "${field}" must be a whole number`);
        } else if (n > 0) {
          groupSelected = true;
        }
      });
      if (groupSelected) {
        complexTypes.push(group.label);
        complexCounts[group.key] += 1;
      }
    });
    row._category = complexTypes.length > 0 ? PRODUCT_CATEGORY.complex : PRODUCT_CATEGORY.btl;
    row._complexTypes = complexTypes;
    if (complexTypes.length > 0) complexRowCount += 1;
    const productCode = getValueByHeader(row, "Lender Product Code *");
    if (productCode) productCategories[productCode] = row._category;

    // 2) Required fields validation - Numeric required
    const numericRequired = [
      { header: "Mortgage Term (years) *", name: "Mortgage Term (years) *", isInt: true },
      { header: "Product Term (Years) *", name: "Product Term (Years) *", isInt: true },
      { header: "LTV (%) *", name: "LTV (%) *" },
      { header: "Min Loan (£) *", name: "Min Loan (£) *" },
      { header: "Max Loan (£) *", name: "Max Loan (£) *" },
      { header: "Product Rate (%) *", name: "Product Rate (%) *" },
      { header: "APRC (%) *", name: "APRC (%) *" },
      { header: "SVR Margin (%) *", name: "SVR Margin (%) *" },
      { header: "Basic Tax Payer DSC (%) *", name: "Basic Tax Payer DSC (%) *" },
      { header: "High Tax Payer DSC (%) *", name: "High Tax Payer DSC (%) *" },
      { header: "ICR Rate (%) *", name: "ICR Rate (%) *" },
      { header: "Application Fee (£) *", name: "Application Fee (£) *" },
    ];

    numericRequired.forEach(({ header, name, isInt }) => {
      const value = getValueByHeader(row, header);
      const cleanValue = stripCommas(value);
      if (cleanValue === "" || isNaN(Number(cleanValue))) {
        errors.push(`Row ${rowNum}: Missing required field "${name}"`);
      } else if (isInt && !Number.isInteger(Number(cleanValue))) {
        errors.push(`Row ${rowNum}: "${name}" must be an integer`);
      }
    });

    // Text/date required fields
    const productName = getValueByHeader(row, "Product Name *");
    if (!productName) {
      errors.push(`Row ${rowNum}: Missing required field "Product Name *"`);
    } else if (productName.length > 120) {
      errors.push(`Row ${rowNum}: "Product Name *" exceeds maximum length of 120 characters`);
    }

    const lenderCode = getValueByHeader(row, "Lender Product Code *");
    if (!lenderCode) {
      errors.push(`Row ${rowNum}: Missing required field "Lender Product Code *"`);
    } else if (lenderCode.length > 60) {
      errors.push(`Row ${rowNum}: "Lender Product Code *" exceeds maximum length of 60 characters`);
    } else if (!/^[a-zA-Z0-9\-_]+$/.test(lenderCode)) {
      errors.push(`Row ${rowNum}: "Lender Product Code *" must contain only letters, numbers, hyphens, and underscores`);
    }

    // Product Expiry Date validation (optional - can be empty or zero)
    const expiryDate = getValueByHeader(row, "Product Expiry Date *");
    const trimmedExpiryDate = expiryDate ? expiryDate.trim() : "";
    
    // Allow empty or zero values - only validate if a valid date value is provided
    // Skip validation for: empty string, "0", "00/00/0000", "0/0/0", or any variation of zeros
    if (trimmedExpiryDate && 
        trimmedExpiryDate !== "" && 
        trimmedExpiryDate !== "0" &&
        !/^0+\/0+\/0+$/.test(trimmedExpiryDate.replace(/\s/g, ""))) { // Skip patterns like "0/0/0", "00/00/0000", etc.
      
      // Parse dd/mm/yyyy format
      const dateParts = trimmedExpiryDate.split("/");
      if (dateParts.length !== 3) {
        errors.push(`Row ${rowNum}: "Product Expiry Date *" must be in dd/mm/yyyy format`);
      } else {
        const [dd, mm, yy] = dateParts.map(Number);

        // Check if all parts are zero or invalid before validating ranges
        if (dd === 0 && mm === 0 && yy === 0) {
          // All zeros - treat as empty/valid
          return; // Skip validation for "0/0/0" format
        }

        // Validate year range (2000-2100)
        if (isNaN(yy) || yy < 2000 || yy > 2100) {
          errors.push(`Row ${rowNum}: "Product Expiry Date *" year must be between 2000 and 2100`);
        }

        // Validate month range
        if (isNaN(mm) || mm < 1 || mm > 12) {
          errors.push(`Row ${rowNum}: "Product Expiry Date *" month must be between 1 and 12`);
        }

        // Validate day range
        if (isNaN(dd) || dd < 1 || dd > 31) {
          errors.push(`Row ${rowNum}: "Product Expiry Date *" day must be between 1 and 31`);
        }

        // Only proceed with date validation if all parts are valid numbers
        if (!isNaN(dd) && !isNaN(mm) && !isNaN(yy) && dd >= 1 && dd <= 31 && mm >= 1 && mm <= 12 && yy >= 2000 && yy <= 2100) {
          // Create date object and validate it's a real date
          const picked = new Date(yy, mm - 1, dd);
          if (
            picked.getFullYear() !== yy ||
            picked.getMonth() !== mm - 1 ||
            picked.getDate() !== dd
          ) {
            errors.push(`Row ${rowNum}: "Product Expiry Date *" is not a valid date`);
          } else {
            // Check if date is in the past
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const selectedDate = new Date(yy, mm - 1, dd);

            if (selectedDate < today) {
              errors.push(`Row ${rowNum}: "Product Expiry Date *" cannot be in the past`);
            }

            // Check if date is at least 24 hours from now
            const min = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
            if (selectedDate < min) {
              const minStr = `${String(min.getDate()).padStart(2, "0")}/${String(min.getMonth() + 1).padStart(2, "0")}/${min.getFullYear()}`;
              errors.push(`Row ${rowNum}: "Product Expiry Date *" must be at least 24 hours from now (≥ ${minStr})`);
            }

            // Check if date is not too far in the future (max 20 years)
            const maxDate = new Date(now.getFullYear() + 20, now.getMonth(), now.getDate());
            if (selectedDate > maxDate) {
              errors.push(`Row ${rowNum}: "Product Expiry Date *" cannot be more than 20 years in the future`);
            }
          }
        }
      }
    }
    // If expiryDate is empty, "0", or "0/0/0" format, it's valid - no validation needed

    // 3) Numeric range + relationship validations (mirrored from ManualEntryPage)
    const mortgageTerm = Number(stripCommas(getValueByHeader(row, "Mortgage Term (years) *")));
    const productTerm = Number(stripCommas(getValueByHeader(row, "Product Term (Years) *")));

    // Mortgage Term: Integer, 0-40 years (mirrored from ManualEntryPage)
    const mortgageTermVal = getValueByHeader(row, "Mortgage Term (years) *");
    if (mortgageTermVal === "") {
      errors.push(`Row ${rowNum}: "Mortgage Term (years) *" is required`);
    } else if (!isInteger(mortgageTermVal)) {
      errors.push(`Row ${rowNum}: "Mortgage Term (years) *" must be a whole number`);
    } else {
      const n = Number(stripCommas(mortgageTermVal));
      if (n < 0 || n > 40) {
        errors.push(`Row ${rowNum}: "Mortgage Term (years) *" must be between 0 and 40`);
      }
    }

    // Product Term: Required, but no specific range (mirrored from ManualEntryPage)
    const productTermVal = getValueByHeader(row, "Product Term (Years) *");
    if (productTermVal === "") {
      errors.push(`Row ${rowNum}: "Product Term (Years) *" is required`);
    }

    // Mortgage Term >= Product Term relationship
    if (mortgageTermVal !== "" && productTermVal !== "" && !isNaN(mortgageTerm) && !isNaN(productTerm)) {
      if (mortgageTerm < productTerm) {
        errors.push(`Row ${rowNum}: "Mortgage Term (years) *" must be greater than or equal to "Product Term (Years) *"`);
      }
    }

    // LTV: 0-100% (mirrored from ManualEntryPage)
    const ltvVal = getValueByHeader(row, "LTV (%) *");
    if (ltvVal === "") {
      errors.push(`Row ${rowNum}: "LTV (%) *" is required`);
    } else {
      const n = Number(stripCommas(ltvVal));
      if (isNaN(n)) {
        errors.push(`Row ${rowNum}: "LTV (%) *" must be a number`);
      } else if (n < 0) {
        errors.push(`Row ${rowNum}: "LTV (%) *" cannot be negative`);
      } else if (n > 100) {
        errors.push(`Row ${rowNum}: "LTV (%) *" cannot exceed 100%`);
      }
    }

    // Min/Max Loan: Non-negative, Max >= Min (mirrored from ManualEntryPage)
    const minLoanVal = getValueByHeader(row, "Min Loan (£) *");
    const maxLoanVal = getValueByHeader(row, "Max Loan (£) *");

    if (minLoanVal === "") {
      errors.push(`Row ${rowNum}: "Min Loan (£) *" is required`);
    } else {
      const n = Number(stripCommas(minLoanVal));
      if (isNaN(n)) {
        errors.push(`Row ${rowNum}: "Min Loan (£) *" must be a number`);
      } else if (n < 0) {
        errors.push(`Row ${rowNum}: "Min Loan (£) *" cannot be negative`);
      }
    }

    if (maxLoanVal === "") {
      errors.push(`Row ${rowNum}: "Max Loan (£) *" is required`);
    } else {
      const n = Number(stripCommas(maxLoanVal));
      if (isNaN(n)) {
        errors.push(`Row ${rowNum}: "Max Loan (£) *" must be a number`);
      } else if (n < 0) {
        errors.push(`Row ${rowNum}: "Max Loan (£) *" cannot be negative`);
      }
    }

    // Max Loan >= Min Loan relationship (mirrored from ManualEntryPage)
    if (minLoanVal !== "" && maxLoanVal !== "" && !isNaN(Number(stripCommas(minLoanVal))) && !isNaN(Number(stripCommas(maxLoanVal)))) {
      if (Number(stripCommas(maxLoanVal)) < Number(stripCommas(minLoanVal))) {
        errors.push(`Row ${rowNum}: "Max Loan (£) *" must be ≥ "Min Loan (£) *"`);
      }
    }

    // Rate/APRC/SVR: Non-negative (mirrored from ManualEntryPage - no max limit)
    const rateFields = [
      { header: "Product Rate (%) *", name: "Product Rate (%) *" },
      { header: "APRC (%) *", name: "APRC (%) *" },
      { header: "SVR Margin (%) *", name: "SVR Margin (%) *" },
    ];

    rateFields.forEach(({ header, name }) => {
      const value = getValueByHeader(row, header);
      if (value === "") {
        errors.push(`Row ${rowNum}: "${name}" is required`);
      } else {
        const n = Number(value);
        if (isNaN(n)) {
          errors.push(`Row ${rowNum}: "${name}" must be a number`);
        } else if (n < 0) {
          errors.push(`Row ${rowNum}: "${name}" cannot be negative`);
        }
      }
    });

    // DSC Basic/High: >= 100% (mirrored from ManualEntryPage)
    const dscBasicVal = getValueByHeader(row, "Basic Tax Payer DSC (%) *");
    if (dscBasicVal === "") {
      errors.push(`Row ${rowNum}: "Basic Tax Payer DSC (%) *" is required`);
    } else {
      const n = Number(stripCommas(dscBasicVal));
      if (isNaN(n)) {
        errors.push(`Row ${rowNum}: "Basic Tax Payer DSC (%) *" must be a number`);
      } else if (n < 100) {
        errors.push(`Row ${rowNum}: "Basic Tax Payer DSC (%) *" must be ≥ 100%`);
      }
    }

    const dscHighVal = getValueByHeader(row, "High Tax Payer DSC (%) *");
    if (dscHighVal === "") {
      errors.push(`Row ${rowNum}: "High Tax Payer DSC (%) *" is required`);
    } else {
      const n = Number(stripCommas(dscHighVal));
      if (isNaN(n)) {
        errors.push(`Row ${rowNum}: "High Tax Payer DSC (%) *" must be a number`);
      } else if (n < 100) {
        errors.push(`Row ${rowNum}: "High Tax Payer DSC (%) *" must be ≥ 100%`);
      }
    }

    // ICR: > 0 (mirrored from ManualEntryPage)
    const icrVal = getValueByHeader(row, "ICR Rate (%) *");
    if (icrVal === "") {
      errors.push(`Row ${rowNum}: "ICR Rate (%) *" is required`);
    } else {
      const n = Number(stripCommas(icrVal));
      if (isNaN(n)) {
        errors.push(`Row ${rowNum}: "ICR Rate (%) *" must be a number`);
      } else if (n <= 0) {
        errors.push(`Row ${rowNum}: "ICR Rate (%) *" must be > 0`);
      }
    }

    // Application Fee: Non-negative (mirrored from ManualEntryPage)
    const appFeeVal = getValueByHeader(row, "Application Fee (£) *");
    if (appFeeVal === "") {
      errors.push(`Row ${rowNum}: "Application Fee (£) *" is required`);
    } else {
      const n = Number(stripCommas(appFeeVal));
      if (isNaN(n)) {
        errors.push(`Row ${rowNum}: "Application Fee (£) *" must be a number`);
      } else if (n < 0) {
        errors.push(`Row ${rowNum}: "Application Fee (£) *" cannot be negative`);
      }
    }

    // 4) Product Fee validation
    // Note: Product Fee structure varies across CSV files
    // Only validate columns that have valid subheaders
    const productFeeHeaderIdx = findColumnIndex(row._headers, "Product Fee *");
    const productFeeIdx = productFeeHeaderIdx === -1 ? -1 : groupStart(row._subheaders, productFeeHeaderIdx);
    if (productFeeIdx !== -1 && productFeeIdx + 2 < row._raw.length) {
      const feePoundRaw = row._raw[productFeeIdx] || "";
      const feePercentRaw = row._raw[productFeeIdx + 1] || "";
      const feeMiniRaw = row._raw[productFeeIdx + 2] || "";

      // Get actual subheaders from the CSV file
      const feePoundSubheader = trimText(row._subheaders[productFeeIdx] || "");
      const feePercentSubheader = trimText(row._subheaders[productFeeIdx + 1] || "");
      const feeMiniSubheader = trimText(row._subheaders[productFeeIdx + 2] || "");

      // Trim values
      const feePound = trimText(feePoundRaw);
      const feePercent = trimText(feePercentRaw);
      const feeMini = trimText(feeMiniRaw);

      // Check if values are subheader text (should be skipped)
      const isPoundSubheader = feePound === feePoundSubheader ||
        feePound === "£" || feePound === "%" || feePound === "Mini Fee";
      const isPercentSubheader = feePercent === feePercentSubheader ||
        feePercent === "£" || feePercent === "%" || feePercent === "Mini Fee";
      const isMiniSubheader = feeMini === feeMiniSubheader ||
        feeMini === "£" || feeMini === "%" || feeMini === "Mini Fee";

      // Check if each column has a valid subheader
      const hasPoundSubheader = feePoundSubheader === "£" || feePoundSubheader.toLowerCase().includes("pound");
      const hasPercentSubheader = feePercentSubheader === "%" || feePercentSubheader.toLowerCase().includes("percent");
      const hasMiniSubheader = feeMiniSubheader === "Mini Fee" || feeMiniSubheader.toLowerCase().includes("mini");

      // Only validate columns that have valid subheaders
      // This prevents validating columns with empty or mismatched subheaders
      if (hasPoundSubheader && feePound !== "" && !isPoundSubheader) {
        if (!isValidNumber(feePound, 0)) {
          errors.push(`Row ${rowNum}: "Product Fee £" must be a number greater than or equal to 0`);
        }
      }

      if (hasPercentSubheader && feePercent !== "" && !isPercentSubheader) {
        if (!isValidNumber(feePercent, 0, 100)) {
          errors.push(`Row ${rowNum}: "Product Fee %" must be a number between 0 and 100`);
        }
      }

      if (hasMiniSubheader && feeMini !== "" && !isMiniSubheader) {
        if (!isValidNumber(feeMini, 0)) {
          errors.push(`Row ${rowNum}: "Product Fee Mini Fee" must be a number greater than or equal to 0`);
        }
      }
    }

    // 5) Valuation Fee + Legal Fee validation
    // Note: Scale and Value/TBC can be in either column - be flexible
    const validateFeeStructure = (mainHeader, feeName, rowNum) => {
      const mainIdx = findColumnIndex(row._headers, mainHeader);
      if (mainIdx === -1 || mainIdx + 2 >= row._raw.length) return;

      // Check subheaders to find Scale and Value/TBC columns
      let scaleSubIdx = -1;
      let valueSubIdx = -1;

      for (let offset = 0; offset <= 2; offset++) {
        const sub = trimText(row._subheaders[mainIdx + offset] || "").toLowerCase();
        if (sub === "scale" && scaleSubIdx === -1) scaleSubIdx = mainIdx + offset;
        if ((sub === "value/tbc" || sub === "value") && valueSubIdx === -1) {
          valueSubIdx = mainIdx + offset;
        }
      }

      // Fallback to original offsets if subheaders not found
      if (scaleSubIdx === -1) scaleSubIdx = mainIdx + 1;
      if (valueSubIdx === -1) valueSubIdx = mainIdx + 2;

      // Guard: if valueSubIdx has overshot into a different field's header column, bail out
      if (valueSubIdx >= row._raw.length) return;
      const valueIdxHeader = trimText(row._headers[valueSubIdx] || "");
      if (valueIdxHeader !== "" && valueIdxHeader !== mainHeader) return;

      // Get actual values from both columns
      const scaleColVal = trimText(row._raw[scaleSubIdx] || "");
      const valueColVal = trimText(row._raw[valueSubIdx] || "");

      let scale = "";
      let value = "";

      // Determine which column contains scale and which contains value
      // Both columns can contain either scale (£, %, TBC) or value (number, TBC)

      // Check if Scale column has scale value
      const scaleColLower = scaleColVal.toLowerCase();
      if (scaleColLower === "£" || scaleColLower === "%" || scaleColLower === "tbc") {
        // Scale column has the scale
        scale = scaleColLower;
        value = valueColVal;
      } else if (scaleColVal !== "" && !isNaN(Number(scaleColVal))) {
        // Scale column has a numeric value - this is the fee value, assume scale is £
        scale = "£";
        value = scaleColVal;
      } else {
        // Scale column is empty or has something else - check Value column
        const valueColLower = valueColVal.toLowerCase();
        if (valueColLower === "£" || valueColLower === "%" || valueColLower === "tbc") {
          // Value column has the scale (columns might be swapped)
          scale = valueColLower;
          value = scaleColVal;
        } else if (valueColVal !== "" && !isNaN(Number(valueColVal))) {
          // Value column has numeric - this is the fee value
          if (scaleColVal === "") {
            // Scale column is empty, assume £
            scale = "£";
            value = valueColVal;
          } else {
            // Both columns have values - use standard structure
            scale = scaleColVal;
            value = valueColVal;
          }
        } else if (valueColLower === "tbc") {
          // Value column has TBC
          scale = "tbc";
          value = valueColVal;
        } else {
          // Both empty or unexpected values
          scale = scaleColVal;
          value = valueColVal;
        }
      }

      // Only validate if we have data
      if (scale === "" && value === "") {
        return; // Both empty is fine (optional field)
      }

      // If we have a value but no scale, try to infer scale
      if (scale === "" && value !== "") {
        const valueLower = value.toLowerCase();
        if (valueLower === "tbc") {
          scale = "tbc";
        } else if (!isNaN(Number(value))) {
          // Numeric value without scale - assume £
          scale = "£";
        } else {
          // Non-numeric, non-TBC value without scale - error
          errors.push(`Row ${rowNum}: "${feeName}" Scale is empty but Value/TBC is provided`);
          return;
        }
      }

      // Validate scale and value combination
      if (scale !== "") {
        const scaleLower = scale.toLowerCase();
        if (scaleLower === "£") {
          if (value !== "" && !isValidNumber(value, 0)) {
            errors.push(`Row ${rowNum}: "${feeName}" Value must be a number ≥ 0 when Scale is £`);
          }
        } else if (scaleLower === "%") {
          if (value !== "" && !isValidNumber(value, 0, 100)) {
            errors.push(`Row ${rowNum}: "${feeName}" Value must be a number between 0 and 100 when Scale is %`);
          }
        } else if (scaleLower === "tbc") {
          const valueNorm = String(value || "").trim().toLowerCase();
          if (valueNorm !== "" && valueNorm !== "tbc") {
            errors.push(`Row ${rowNum}: "${feeName}" Value must be empty or "TBC" when Scale is TBC`);
          }
        } else {
          // Scale is not one of the valid values - only error if it's not empty and not a number (which we already handled)
          if (scale !== "" && !isNaN(Number(scale))) {
            // Scale column has a number (already handled above as value with scale=£)
            // No error needed
          } else if (scale !== "") {
            errors.push(`Row ${rowNum}: "${feeName}" Scale must be one of: £, %, TBC`);
          }
        }
      }
    };

    // Check if Free Valuation is Yes - if so, Valuation Fee should be empty
    const freeValuationIdx = findColumnIndex(row._headers, "Free Valuation");
    let isFreeValuationYes = false;
    if (freeValuationIdx !== -1) {
      const freeValuationMain = trimText(row._raw[freeValuationIdx] || "");
      // Only the two-column (Yes | No) layout has a separate "Yes" column; otherwise the next column is another field
      const hasYesColumn = trimText(row._subheaders[freeValuationIdx + 1] || "").toLowerCase() === "yes" &&
        trimText(row._headers[freeValuationIdx + 1] || "") === "";
      const freeValuationYes = hasYesColumn ? trimText(row._raw[freeValuationIdx + 1] || "") : "";

      // Check if Free Valuation is Yes (in main column or Yes sub-column)
      isFreeValuationYes = isYes(freeValuationMain) || isYes(freeValuationYes);
    }

    // Validate Valuation Fee (but skip if Free Valuation is Yes)
    if (!isFreeValuationYes) {
      validateFeeStructure("Valuation Fee", "Valuation Fee", rowNum);
    } else {
      // Free Valuation is Yes - check if Valuation Fee has non-zero values
      const valuationFeeHeaderIdx = findColumnIndex(row._headers, "Valuation Fee");
      const valuationFeeIdx = valuationFeeHeaderIdx === -1 ? -1 : groupStart(row._subheaders, valuationFeeHeaderIdx);
      if (valuationFeeIdx !== -1 && valuationFeeIdx + 1 < row._raw.length) {
        const valuationFeeScale = trimText(row._raw[valuationFeeIdx] || "");
        const valuationFeeValue = trimText(row._raw[valuationFeeIdx + 1] || "");

        // Treat "0", empty string, and "TBC" as empty/valid
        // Only error if there are actual non-zero values
        const scaleNum = Number(valuationFeeScale);
        const valueNum = Number(valuationFeeValue);
        
        const hasNonZeroScale = valuationFeeScale !== "" && 
          valuationFeeScale !== "0" && 
          valuationFeeScale.toLowerCase() !== "tbc" &&
          !(valuationFeeScale.toLowerCase() === "£" || valuationFeeScale.toLowerCase() === "%") &&
          (isNaN(scaleNum) ? true : scaleNum !== 0);
        
        const hasNonZeroValue = valuationFeeValue !== "" && 
          valuationFeeValue !== "0" && 
          valuationFeeValue.toLowerCase() !== "tbc" &&
          (isNaN(valueNum) ? true : valueNum !== 0);
        
        // Only error if there are actual non-zero values (not "0", not empty, not "TBC")
        if (hasNonZeroScale || hasNonZeroValue) {
          errors.push(`Row ${rowNum}: "Valuation Fee" should not be entered when "Free Valuation" is "Yes"`);
        }
      }
    }

    validateFeeStructure("Legal Fee", "Legal Fee", rowNum);

    // 6) Early Repayment Charges validation
    // Only validate if the structure matches expected format to avoid reading wrong columns
    const ercHeaderIdx = findColumnIndex(row._headers, "Early Repayment Charges");
    const ercIdx = ercHeaderIdx === -1 ? -1 : groupStart(row._subheaders, ercHeaderIdx);
    if (ercIdx !== -1 && ercIdx + 4 < row._raw.length) {
      const ercValues = [0, 1, 2, 3, 4].map(i => trimText(row._raw[ercIdx + i] || ""));

      // Get subheader values to check if we're reading the correct columns
      const ercSubheaders = [0, 1, 2, 3, 4].map(i => trimText(row._subheaders[ercIdx + i] || ""));

      // Check if subheaders match expected format (Year 1 (%), Year 2 (%), etc.)
      // This prevents reading wrong columns when CSV structure is different
      // const expectedSubheaders = ["Year 1 (%)", "Year 2 (%)", "Year 3 (%)", "Year 4 (%)", "Year 5 (%)"];
      const hasValidStructure = ercSubheaders.some((sub, idx) =>
        sub.includes("Year") && sub.includes(String(idx + 1))
      );

      // Only validate if structure matches expected format
      if (hasValidStructure) {
        // ERC years are optional (e.g. no-ERC discount products, or ERCs for fewer years than the term)
        ercValues.forEach((val, idx) => {
          // Skip if this is a subheader value
          if (val === ercSubheaders[idx]) {
            return;
          }

          if (val !== "") {
            const n = Number(val);
            if (isNaN(n)) {
              errors.push(`Row ${rowNum}: "Year ${idx + 1} (%)" must be a number`);
            } else if (n < 0) {
              errors.push(`Row ${rowNum}: "Year ${idx + 1} (%)" cannot be negative`);
            } else if (n > 100) {
              // Previously logged a warning when ERC year > 100%; now suppressed.
            }
          }
        });
      }
      // If structure doesn't match, skip ERC validation (CSV format varies)
    }

    // 7) Cashback validation (mirrored from ManualEntryPage - non-negative)
    const cashback = getValueByHeader(row, "Cashback (£)");
    if (cashback !== "") {
      const n = Number(stripCommas(cashback));
      if (isNaN(n)) {
        errors.push(`Row ${rowNum}: "Cashback (£)" must be a number`);
      } else if (n < 0) {
        errors.push(`Row ${rowNum}: "Cashback (£)" cannot be negative`);
      }
    }

    // 8) Yes/No choice validations
    // Product Type: exactly one Yes
    // Note: In the CSV, "Yes" might be in the main column, and sub-columns might be empty
    const productTypeHeaderIdx = findColumnIndex(row._headers, "Product Type *");
    if (productTypeHeaderIdx !== -1) {
      const start = groupStart(row._subheaders, productTypeHeaderIdx);
      const optionValues = [0, 1, 2, 3].map(i => trimText(row._raw[start + i] || ""));
      const mainValue = trimText(row._raw[productTypeHeaderIdx] || "");

      // Legacy layout allows "Yes" in the (sub-header-less) main column
      const legacyMainYes = start !== productTypeHeaderIdx && isYes(mainValue);
      if (!legacyMainYes && countYes(optionValues) !== 1) {
        errors.push(`Row ${rowNum}: "Product Type *" must have exactly one "Yes" (Fixed, Variable, Tracker, or Discounted)`);
      }
    }

    // Loan Purpose: previously required at least one "Yes" – now optional
    const loanPurposeIdx = findColumnIndex(row._headers, "Loan Purpose *");
    if (loanPurposeIdx !== -1) {
      // Validation relaxed: allow zero or multiple selections without error
    }

    // Borrower Type: previously required at least one "Yes" – now optional
    const borrowerTypeIdx = findColumnIndex(row._headers, "Borrower Type *");
    if (borrowerTypeIdx !== -1 && borrowerTypeIdx + 3 < row._raw.length) {
      // Validation relaxed: allow zero or multiple selections without error
    }

    // EPC: supports single-column "Yes/No" OR 2-column Yes/No
    const epcIdx = findColumnIndex(row._headers, "EPC rating — Is it a green product? *");
    if (epcIdx !== -1) {
      const s0 = trimText(row._subheaders[epcIdx] || "").toLowerCase();
      const s1 = trimText(row._subheaders[epcIdx + 1] || "").toLowerCase();

      const v0 = trimText(row._raw[epcIdx] || "");
      const v1 = trimText(row._raw[epcIdx + 1] || "");

      // ✅ Single-column format: subheader is "Yes/No"
      if (s0 === "yes/no" || s0 === "yes / no") {
        const val = v0.toLowerCase();
        if (val !== "yes" && val !== "no") {
          errors.push(`Row ${rowNum}: "EPC rating — Is it a green product? *" must be "Yes" or "No"`);
        }
      }
      // ✅ Two-column format: subheaders are "Yes" and "No"
      else if (s0 === "yes" && s1 === "no") {
        const selectedCount = countSelected([v0, v1], [row._subheaders[epcIdx], row._subheaders[epcIdx + 1]]);
        if (selectedCount !== 1) {
          errors.push(`Row ${rowNum}: "EPC rating — Is it a green product? *" must have exactly one "Yes"`);
        }
      }
      // Otherwise: structure doesn't match, skip or warn
    }

    // Home Owner: single-column "Yes/No" OR 2-column Yes/No
    const homeOwnerIdx = findColumnIndex(row._headers, "Home Owner *");
    if (homeOwnerIdx !== -1) {
      const s0 = trimText(row._subheaders[homeOwnerIdx] || "").toLowerCase();
      const s1 = trimText(row._subheaders[homeOwnerIdx + 1] || "").toLowerCase();
      const v0 = trimText(row._raw[homeOwnerIdx] || "");
      const v1 = trimText(row._raw[homeOwnerIdx + 1] || "");
      if (s0 === "yes/no" || s0 === "yes / no") {
        const val = v0.toLowerCase();
        if (val !== "yes" && val !== "no") {
          errors.push(`Row ${rowNum}: "Home Owner *" must be "Yes" or "No"`);
        }
      } else if (s0 === "yes" && s1 === "no") {
        const selectedCount = countSelected([v0, v1], [row._subheaders[homeOwnerIdx], row._subheaders[homeOwnerIdx + 1]]);
        if (selectedCount !== 1) {
          errors.push(`Row ${rowNum}: "Home Owner *" must have exactly one "Yes"`);
        }
      }
    }

    // BTL Experience: previously required exactly one "Yes" – now optional
    const btlExpIdx = findColumnIndex(row._headers, "BTL Experience (years) *");
    if (btlExpIdx !== -1 && btlExpIdx + 2 < row._raw.length) {
      // Validation relaxed: allow zero or multiple selections without error
    }

    // Credit Worthiness: previously required at least one "Yes" – now optional
    const creditIdx = findColumnIndex(row._headers, "Credit Worthiness *");
    if (creditIdx !== -1 && creditIdx + 2 < row._raw.length) {
      // Validation relaxed: allow zero or multiple selections without error
    }

    // Tenancy Type: previously required at least one "Yes" – now optional
    const tenancyIdx = findColumnIndex(row._headers, "Tenancy Type *");
    if (tenancyIdx !== -1 && tenancyIdx + 3 < row._raw.length) {
      // Validation relaxed: allow zero or multiple selections without error
    }

    // F/H or L/H: previously required at least one "Yes" – now optional
    const tenureIdx = findColumnIndex(row._headers, "F/H or L/H *");
    let isLeaseholdSelected = false;
    if (tenureIdx !== -1) {
      const mainValue = trimText(row._raw[tenureIdx] || "");
      // Subheaders are in the same column (Freehold) and next column (Leasehold)
      // Column structure: [tenureIdx] = "F/H or L/H" header, subheader "Freehold"
      //                  [tenureIdx+1] = empty header, subheader "Leasehold"
      const tenureValues = tenureIdx + 1 < row._raw.length ? [
        trimText(row._raw[tenureIdx] || ""), // Freehold subheader column (same as main header)
        trimText(row._raw[tenureIdx + 1] || ""), // Leasehold subheader column (next column)
      ] : [];

      // Check if "Yes" is in main column (valid format)
      if (isYes(mainValue)) {
        // Main column has Yes, which is acceptable
        // Try to determine if it's Leasehold by checking sub-columns or main value
        isLeaseholdSelected = tenureValues[1] === "Yes" || mainValue.toLowerCase().includes("leasehold");
      } else {
        // Check sub-columns
        const tenureYesCount = countYes(tenureValues);
        // Validation relaxed: allow zero or multiple selections without error
        // Still determine if Leasehold is selected (second sub-column is Leasehold)
        if (tenureYesCount > 0) {
          isLeaseholdSelected = isYes(tenureValues[1]);
        }
      }
    }

    // Leasehold remaining term: Required only if Leasehold is selected
    const leaseholdTermIdx = findColumnIndex(row._headers, "Leasehold remaining term");
    if (leaseholdTermIdx !== -1) {
      if (isLeaseholdSelected) {
        // If Leasehold is selected, validate the remaining term
        // The "Years" subheader is in the same column as the main header (column 54)
        const termValue = trimText(row._raw[leaseholdTermIdx] || "");

        if (termValue === "") {
          errors.push(`Row ${rowNum}: "Leasehold remaining term" is required when "F/H or L/H" is "Leasehold"`);
        } else {
          // Validate it's a valid number (years)
          const termNum = Number(termValue);
          if (isNaN(termNum)) {
            errors.push(`Row ${rowNum}: "Leasehold remaining term" must be a number (years)`);
          } else if (termNum < 0) {
            errors.push(`Row ${rowNum}: "Leasehold remaining term" cannot be negative`);
          } else if (termNum > 999) {
            errors.push(`Row ${rowNum}: "Leasehold remaining term" seems unusually high (${termNum} years). Please verify.`);
          }
        }
      }
      // If Freehold is selected, Leasehold remaining term can be empty (optional)
    }

    // Ex Local Authority: single-column "Yes/No" OR 2-column Yes/No
    const exLocalIdx = findColumnIndex(row._headers, "Ex Local Authority *");
    if (exLocalIdx !== -1) {
      const s0 = trimText(row._subheaders[exLocalIdx] || "").toLowerCase();
      const s1 = trimText(row._subheaders[exLocalIdx + 1] || "").toLowerCase();
      const v0 = trimText(row._raw[exLocalIdx] || "");
      const v1 = trimText(row._raw[exLocalIdx + 1] || "");
      if (s0 === "yes/no" || s0 === "yes / no") {
        const val = v0.toLowerCase();
        if (val !== "yes" && val !== "no") {
          errors.push(`Row ${rowNum}: "Ex Local Authority *" must be "Yes" or "No"`);
        }
      } else if (s0 === "yes" && s1 === "no") {
        const selectedCount = countSelected([v0, v1], [row._subheaders[exLocalIdx], row._subheaders[exLocalIdx + 1]]);
        if (selectedCount !== 1) {
          errors.push(`Row ${rowNum}: "Ex Local Authority *" must have exactly one "Yes"`);
        }
      }
    }

    // Deposit Origination: previously required at least one "Yes" – now optional
    const depositIdx = findColumnIndex(row._headers, "Deposit Origination *");
    if (depositIdx !== -1) {
      // Validation relaxed: allow zero or multiple selections without error
    }

    // Day 1 Remortgage: previously required at least one "Yes" – now optional
    const day1Idx = findColumnIndex(row._headers, "Day 1 Remortgage *");
    if (day1Idx !== -1 && day1Idx + 3 < row._raw.length) {
      // Validation relaxed: allow zero or multiple selections without error
    }

    // Standalone Yes/No fields: Optional (can have defaults)
    // Supports single-column "Yes/No" (value = Yes, No, or empty) OR 2-column Yes/No
    const standaloneFields = [
      { header: "Portable", name: "Portable" },
      { header: "Overpayments", name: "Overpayments" },
      { header: "Underpayments", name: "Underpayments" },
      { header: "Payment Holiday", name: "Payment Holiday" },
      { header: "Free Legals", name: "Free Legals" },
      { header: "Free Valuation", name: "Free Valuation" },
    ];

    standaloneFields.forEach(({ header, name }) => {
      const fieldIdx = findColumnIndex(row._headers, header);
      if (fieldIdx === -1) return;
      const s0 = trimText(row._subheaders[fieldIdx] || "").toLowerCase();
      const s1 = trimText(row._subheaders[fieldIdx + 1] || "").toLowerCase();
      const v0 = trimText(row._raw[fieldIdx] || "");
      const v1 = trimText(row._raw[fieldIdx + 1] || "");
      // Single-column format: subheader "Yes/No" or "Yes/no"
      if (s0 === "yes/no" || s0 === "yes / no") {
        const val = v0.toLowerCase();
        if (val !== "" && val !== "yes" && val !== "no") {
          errors.push(`Row ${rowNum}: "${name}" must be "Yes", "No", or empty`);
        }
      }
      // Two-column format: subheaders "Yes" and "No"
      else if (s0 === "yes" && s1 === "no" && fieldIdx + 1 < row._raw.length) {
        const selectedCount = countSelected([v0, v1], [row._subheaders[fieldIdx], row._subheaders[fieldIdx + 1]]);
        if (selectedCount > 1) {
          errors.push(`Row ${rowNum}: "${name}" cannot have both "Yes" and "No" columns selected`);
        }
      }
    });
  });

  return {
    valid: errors.length === 0,
    errors,
    warnings,
    data: parsed,
    productCategories,
    stats: {
      totalRows: rows.length,
      btlRows: rows.length - complexRowCount,
      complexBtlRows: complexRowCount,
      complexBreakdown: complexGroups.map(g => ({ key: g.key, label: g.label, rows: complexCounts[g.key] })),
      totalHeaders: headers.length,
    },
  };
}